import React, { createContext, useContext } from 'react';

import { DiscountCode } from 'src/services/rails-service';

export type DiscountContextInterface = {
  eligibleDiscount?: {
    publicDescription?: string;
    totalMealMinimumCopy?: string;
    discountCode: string;
  };
};

const DiscountContext = createContext<DiscountContextInterface | null>(null);

export const DiscountProvider: React.FC<{
  discountCode?: DiscountCode;
  totalNumMeals: number;
  children: React.ReactNode;
}> = ({ discountCode, totalNumMeals, children }) => {
  if (!discountCode) {
    return (
      <DiscountContext.Provider
        value={{
          eligibleDiscount: undefined,
        }}
      >
        {children}
      </DiscountContext.Provider>
    );
  }

  return (
    <DiscountContext.Provider
      value={{
        eligibleDiscount: {
          publicDescription: discountCode.publicDescription,
          totalMealMinimumCopy: getTotalMinimumCopy({
            discountCode: discountCode,
            totalNumMeals,
          }),
          discountCode: discountCode.code,
        },
      }}
    >
      {children}
    </DiscountContext.Provider>
  );
};

function getTotalMinimumCopy(input: {
  discountCode: DiscountCode;
  totalNumMeals: number;
}): string | undefined {
  const { discountCode, totalNumMeals } = input;

  // Compute totalMealMinimumCopy based on eligibility and meal count
  let totalMealMinimumCopy: string | undefined;
  const discountTotalMealMinimum = discountCode?.totalMealMinimum;

  // This is specifically checking for truthy -- if it's 0, we don't want to show the banner
  if (discountTotalMealMinimum) {
    const neededCount = discountTotalMealMinimum - totalNumMeals;

    if (neededCount > 0) {
      const mealOrMeals = neededCount === 1 ? 'meal' : 'meals';
      totalMealMinimumCopy = `${neededCount} ${mealOrMeals} until your discount is applied!`;
    }

    if (neededCount < 1) {
      return undefined;
    }
  }

  return totalMealMinimumCopy;
}

/**
 * Note: this hook is a bit weird with it's default return if there's no context. This is to
 * handle the fact that some child components will attempt to call this provider (in EditMeals)
 * that never have their parent components wrapped in DiscountContext.
 *
 * It'd take a large amount of effort to split apart these components right now, so I'm making
 * this compromise.
 */
export function useDiscount(): DiscountContextInterface {
  const context = useContext(DiscountContext);
  return context ?? { eligibleDiscount: undefined };
}

export default DiscountContext;
